import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, message, Dropdown, Tooltip } from 'antd';
import { EditOutlined, SaveFilled, CopyOutlined } from '@ant-design/icons';
import MarkdownPreview from '@uiw/react-markdown-preview';

import './css/StoryPresenter.css';
import './css/MarkDownStyles.css';

import {
  BoldItalicUnderlineToggles,
  UndoRedo,
  MDXEditor,
  toolbarPlugin,
  listsPlugin,
  ListsToggle,
  tablePlugin,
  InsertTable,
  BlockTypeSelect,
  headingsPlugin,
  quotePlugin,
  linkPlugin,
  linkDialogPlugin,
  Separator,
  thematicBreakPlugin,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';

import { useRealmObjectContext } from '../contexts/RealmObjectProvider';

const StoryPresenter = ({ fieldKey, StoryLabel, storySize = 'small-story' }) => {
  const { Text, Title } = Typography;
  const { realmObject, updateRealmObject } = useRealmObjectContext();

  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [localContent, setLocalContent] = useState('');
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);

  // Refs to get rendered HTML for copy-HTML mode (even while editing)
  const visiblePreviewRef = useRef(null);
  const hiddenPreviewRef = useRef(null);

  useEffect(() => {
    setIsLoading(true);
    setIsEditing(false);
    const initialContent =
      (realmObject && realmObject.payload && realmObject.payload[fieldKey]) || '';
    setLocalContent(initialContent);
    setIsLoading(false);
  }, [realmObject, fieldKey]);

  const toggleEditMode = () => {
    if (isEditing) {
      updateRealmObject(fieldKey, localContent);
    }
    setIsEditing(!isEditing);
  };

  const handleContentChange = (newValue) => {
    setLocalContent(newValue);
  };

  // --------- Copy helpers ----------
  const copyMarkdown = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        message.success('Markdown copied to clipboard!');
        return true;
      } catch { /* fall through */ }
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      ok ? message.success('Markdown copied to clipboard!') : message.error('Failed to copy markdown.');
      return ok;
    } catch {
      message.error('Failed to copy markdown. (Use HTTPS or allow clipboard.)');
      return false;
    }
  };

  const copyHTML = async (html, plainFallback) => {
    // 🧹 scrub inline background-color so Word doesn’t paint the page
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    tempDiv.querySelectorAll('*').forEach((el) => {
      if (el.style && el.style.backgroundColor) el.style.backgroundColor = '';
    });
    const cleanHtml = tempDiv.innerHTML;

    if (navigator.clipboard && window.ClipboardItem && window.isSecureContext) {
      try {
        const blobHtml = new Blob([cleanHtml], { type: 'text/html' });
        const blobText = new Blob([plainFallback], { type: 'text/plain' });
        await navigator.clipboard.write([
          new window.ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobText }),
        ]);
        message.success('Formatted HTML copied (no background)!');
        return true;
      } catch { /* fall through */ }
    }

    // Legacy: hidden contentEditable with clean HTML
    try {
      const ghost = document.createElement('div');
      ghost.contentEditable = 'true';
      ghost.style.position = 'fixed';
      ghost.style.top = '-9999px';
      ghost.style.opacity = '0';
      ghost.innerHTML = cleanHtml;
      document.body.appendChild(ghost);

      const range = document.createRange();
      range.selectNodeContents(ghost);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);

      const ok = document.execCommand('copy');

      sel.removeAllRanges();
      document.body.removeChild(ghost);

      ok ? message.success('Formatted HTML copied!') : message.error('Copy blocked by browser.');
      return ok;
    } catch {
      message.error('Copy blocked by browser/security settings.');
      return false;
    }
  };

  const doCopy = async (mode) => {
    if (mode === 'markdown') {
      await copyMarkdown(localContent ?? '');
      return;
    }
    const container = (!isEditing && visiblePreviewRef.current) || hiddenPreviewRef.current;
    if (!container) {
      message.error('Nothing to copy yet.');
      return;
    }
    const html = container.innerHTML;
    const plain = container.innerText || (localContent ?? '');
    await copyHTML(html, plain);
  };
  // --------- End copy helpers ----------

  // Dropdown menu items; selecting triggers copy, then closes menu
  const copyMenu = {
    items: [
      { key: 'markdown', label: 'Copy Markdown' },
      { key: 'html', label: 'Copy HTML' },
    ],
    onClick: async ({ key }) => {
      await doCopy(key);
      setCopyMenuOpen(false);
    },
  };

  return (
    <div className="story-presenter">
      {/* Actions pinned top-right */}
      <div className="story-header">
        <Title className="custom-title" level={5}>
          {StoryLabel}
        </Title>
        
        <div className="button-group">
          <Tooltip title={isEditing ? 'Save' : 'Edit'}>
            <Button
              icon={isEditing ? <SaveFilled /> : <EditOutlined />}
              onClick={toggleEditMode}
              aria-label={isEditing ? 'Save' : 'Edit'}
            />
          </Tooltip>

          <Dropdown
            menu={copyMenu}
            trigger={['click']}
            open={copyMenuOpen}
            onOpenChange={setCopyMenuOpen}
            placement="bottomRight"
          >
            <Tooltip title="Copy…">
              <Button
                icon={<CopyOutlined />}
                aria-label="Copy"
                onClick={() => setCopyMenuOpen((o) => !o)}
              />
            </Tooltip>
          </Dropdown>
        </div>
      </div>


      {/* Visible preview (view mode) */}
      {!isEditing ? (
        <div ref={visiblePreviewRef}>
          <MarkdownPreview
            className="markdown-previewer"
            style={{ border: '1px solid', borderRadius: '8px' }}
            source={localContent}
          />
        </div>
      ) : (
        <MDXEditor
          plugins={[
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  <BlockTypeSelect />
                  <BoldItalicUnderlineToggles />
                  <Separator />
                  <ListsToggle />
                  <InsertTable />
                  <Separator />
                  <UndoRedo />
                </>
              ),
            }),
            headingsPlugin(),
            quotePlugin(),
            linkPlugin(),
            linkDialogPlugin(),
            listsPlugin(),
            tablePlugin(),
            thematicBreakPlugin(),
          ]}
          markdown={localContent}
          onChange={handleContentChange}
          className={storySize + ' custom-editor'}
          contentEditableClassName="custom-markdown"
        />
      )}

      {/* Hidden preview for HTML copy during edit */}
      <div
        ref={hiddenPreviewRef}
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none' }}
      >
        <MarkdownPreview source={localContent} />
      </div>

      {!((realmObject && realmObject.payload && realmObject.payload[fieldKey])) && !isEditing && (
        <Text className="text-highlight text-primary element-margined-small">
          "Press Edit or mosey over to the Foundry"
        </Text>
      )}
    </div>
  );
};

export default StoryPresenter;
